
import { useEffect, useRef, useState } from 'react';

/**
 * BrandStory component
 *
 * Shows a brand description with a fade‑in animation when the component
 * scrolls into view. The IntersectionObserver API is used, with a graceful
 * fallback for environments where it is not available.
 */
export default function BrandStory() {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    // Guard against browsers that do not support IntersectionObserver
    if (!('IntersectionObserver' in window)) {
      // If the API is missing we simply show the content immediately.
      setIsVisible(true);
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        // Defensive check – entry can be undefined in rare edge‑cases.
        if (entry) {
          setIsVisible(entry.isIntersecting);
        }
      },
      { threshold: 0.3 }
    );

    const current = sectionRef.current;
    if (current) {
      observer.observe(current);
    }

    // Cleanup: stop observing and disconnect the observer.
    return () => {
      if (current) {
        observer.unobserve(current);
      }
      observer.disconnect();
    };
  }, []);

  return (
    <section
      ref={sectionRef}
      id="about"
      className="min-h-screen bg-white py-20 px-6 md:px-12"
    >
      <div className="max-w-4xl mx-auto">
        <div
          className={`transition-all duration-1000 ease-out ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
        >
          <div className="grid md:grid-cols-1 gap-12 items-center mt-16">
            {/* Logo Image */}
            <div
              className={`flex justify-center mb-8 transition-all duration-1000 delay-300 ease-out ${
                isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
              }`}
            >
              <img
                src="https://static.readdy.ai/image/5de3749ad0c20aa6952a12946939f686/3078c8d3eabb61253ac3885d63ca8b04.png"
                alt="MILAN Brand Logo"
                className="w-40 h-40 object-contain"
              />
            </div>

            <div
              className={`transition-all duration-1000 delay-500 ease-out ${
                isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-8'
              }`}
            >
              <p className="text-base text-gray-700 leading-relaxed mb-4">
                MILANはシンプルでありながらもトレンディなアイテムを提案しています。
              </p>
              <p className="text-base text-gray-700 leading-relaxed mb-4">
                ファッションを楽しむ女性のために長く愛用できるベーシックアイテムを豊富に取り揃え、エレガントで控えめな雰囲気を演出します。
              </p>
              <p className="text-base text-gray-700 leading-relaxed">
                そして、MILANのアイテムがあなたの心も豊かにしてくれることを願って。
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
